import React, { useEffect, useRef, useState } from "react";
import { Paper } from "@mui/material";
import EnterpriseForm from "./EnterpriseConfig/EnterpriseForm";
import EnterpriseModals from "./EnterpriseConfig/EnterpriseModals";

const normalizeCode = (value) => {
  const only = String(value || "").replace(/\D/g, "").slice(0, 3);
  return only ? only.padStart(3, "0") : "";
};

const normalizeEstablecimientos = (empresa) => {
  if (Array.isArray(empresa?.establecimientos) && empresa.establecimientos.length > 0) {
    return empresa.establecimientos
      .map((e) => {
        const codigo = normalizeCode(e.codigo);
        return {
          id: e.id || null,
          codigo,
          nombre: String(e.nombre || "").trim() || `ESTABLECIMIENTO ${codigo}`,
          direccion: String(e.direccion || "").trim(),
          activo: typeof e.activo === "boolean" ? e.activo : true,
        };
      })
      .filter((e) => e.codigo);
  }

  const raw = String(empresa?.numero_establecimiento || "").trim();

  if (raw) {
    const seen = new Set();

    return raw
      .split(/[,\s;|]+/)
      .map((item) => normalizeCode(item))
      .filter(Boolean)
      .filter((codigo) => {
        if (seen.has(codigo)) return false;
        seen.add(codigo);
        return true;
      })
      .map((codigo) => ({
        id: null,
        codigo,
        nombre: `ESTABLECIMIENTO ${codigo}`,
        direccion: empresa?.direccion || "",
        activo: true,
      }));
  }

  return [];
};

const getNumeroEstablecimientoFromList = (establecimientos) => {
  if (!Array.isArray(establecimientos)) return "";

  return establecimientos
    .map((item) => normalizeCode(item.codigo))
    .filter(Boolean)
    .join(", ");
};

const EnterpriseConfig = () => {
  const [empresa, setEmpresa] = useState({
    id: null,
    ruc: "",
    razon_social: "",
    nombre_comercial: "",
    numero_establecimiento: "",
    establecimientos: [],
    obligado_contabilidad: "NO",
    contribuyente_especial: false,
    gran_contribuyente: false,
    exportador: false,
    exportador_tipo: "NO_HABITUAL",
    contribuyente_rimpe: false,
    rimpe_tipo: "",
    regimen_tipo: "GENERAL",
    regimen_general: true,
    agente_retencion: false,
    ciudad: "",
    telefonos: "",
    direccion: "",
    numero_decimales: 6,
    email_notificacion: "",
    logo_empresa: null,
  });

  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [preview, setPreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const logoRef = useRef(null);

  const getToken = () => localStorage.getItem("access");

  const hydrateEmpresa = (e) => {
    const establecimientos = normalizeEstablecimientos(e);

    setEmpresa((prev) => ({
      ...prev,
      ...e,
      id: e.id ?? prev.id,
      establecimientos,
      numero_establecimiento: getNumeroEstablecimientoFromList(establecimientos),
      exportador: !!e.exportador,
      contribuyente_especial: !!e.contribuyente_especial,
      gran_contribuyente: !!e.gran_contribuyente,
      agente_retencion: !!e.agente_retencion,
      contribuyente_rimpe:
        e.regimen_tipo === "EMPRENDEDOR" ||
        e.regimen_tipo === "NEGOCIO_POPULAR" ||
        e.regimen_tipo === "RIMPE_EMPRENDEDOR" ||
        e.regimen_tipo === "RIMPE_NEGOCIO_POPULAR"
          ? true
          : !!e.contribuyente_rimpe,
      regimen_general: e.regimen_tipo === "GENERAL",
      rimpe_tipo:
        e.regimen_tipo === "EMPRENDEDOR" ||
        e.regimen_tipo === "NEGOCIO_POPULAR" ||
        e.regimen_tipo === "RIMPE_EMPRENDEDOR" ||
        e.regimen_tipo === "RIMPE_NEGOCIO_POPULAR"
          ? e.regimen_tipo
          : "",
      regimen_tipo: e.regimen_tipo || "GENERAL",
      logo_empresa: e.logo_empresa ?? null,
    }));
  };

  useEffect(() => {
    fetch("/enterprise/empresa/obtener/", {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.empresa) {
          hydrateEmpresa(data.empresa);
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEmpresa((p) => {
      const next = { ...p, [name]: value };

      if (name === "regimen_tipo") {
        next.contribuyente_rimpe =
          value === "EMPRENDEDOR" ||
          value === "NEGOCIO_POPULAR" ||
          value === "RIMPE_EMPRENDEDOR" ||
          value === "RIMPE_NEGOCIO_POPULAR";
        next.regimen_general = value === "GENERAL";
        next.rimpe_tipo = next.contribuyente_rimpe ? value : "";
      }

      if (name === "rimpe_tipo") {
        next.regimen_tipo = value;
      }

      return next;
    });
  };

  const handleCheck = (e) => {
    const { name, checked } = e.target;

    setEmpresa((p) => {
      const next = { ...p, [name]: checked };

      if (name === "exportador" && !checked) {
        next.exportador_tipo = "NO_HABITUAL";
      }

      if (name === "contribuyente_rimpe") {
        if (checked) {
          next.regimen_general = false;
          next.regimen_tipo = next.rimpe_tipo || "EMPRENDEDOR";
          next.rimpe_tipo = next.regimen_tipo;
        } else {
          next.rimpe_tipo = "";
          next.regimen_tipo = next.regimen_general ? "GENERAL" : "GENERAL";
        }
      }

      if (name === "regimen_general") {
        if (checked) {
          next.contribuyente_rimpe = false;
          next.rimpe_tipo = "";
          next.regimen_tipo = "GENERAL";
        } else if (!next.contribuyente_rimpe) {
          next.regimen_tipo = "";
        }
      }

      return next;
    });
  };

  const handleFile = (e) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];
      const ext = file.name.split(".").pop().toLowerCase();
      const allowed = ["jpg", "jpeg", "png"];

      if (!allowed.includes(ext)) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Formato no permitido. Solo JPG, JPEG y PNG.",
        });
        e.target.value = "";
        return;
      }

      setEmpresa((p) => ({ ...p, [name]: file }));
    }
  };

  const openPreview = (fileOrUrl) => {
    if (!fileOrUrl) return;

    const url = typeof fileOrUrl === "string" ? fileOrUrl : URL.createObjectURL(fileOrUrl);
    setPreview(url);
    setOpenModal(true);
  };

  const closePreview = () => {
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    setOpenModal(false);
    setPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    const establecimientos = normalizeEstablecimientos(empresa);

    if (!establecimientos.length) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Ingrese al menos un establecimiento válido.",
      });
      return;
    }

    if (empresa.contribuyente_rimpe && !empresa.rimpe_tipo && !empresa.regimen_tipo) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Seleccione el tipo de RIMPE.",
      });
      return;
    }

    const isEdit = !!empresa.id;
    const url = isEdit
      ? `/enterprise/empresa/editar/${empresa.id}/`
      : "/enterprise/empresa/crear/";

    const regimen_tipo = empresa.regimen_general
      ? "GENERAL"
      : empresa.contribuyente_rimpe
      ? empresa.rimpe_tipo || empresa.regimen_tipo || "EMPRENDEDOR"
      : "GENERAL";

    const form = new FormData();

    form.append("ruc", empresa.ruc || "");
    form.append("razon_social", empresa.razon_social || "");
    form.append("nombre_comercial", empresa.nombre_comercial || "");
    form.append("numero_establecimiento", getNumeroEstablecimientoFromList(establecimientos));
    form.append("establecimientos", JSON.stringify(establecimientos));
    form.append("obligado_contabilidad", empresa.obligado_contabilidad || "NO");
    form.append("contribuyente_especial", String(!!empresa.contribuyente_especial));
    form.append("gran_contribuyente", String(!!empresa.gran_contribuyente));
    form.append("exportador", String(!!empresa.exportador));
    form.append("exportador_tipo", empresa.exportador ? empresa.exportador_tipo || "NO_HABITUAL" : "");
    form.append("agente_retencion", String(!!empresa.agente_retencion));
    form.append("ciudad", empresa.ciudad || "");
    form.append("telefonos", empresa.telefonos || "");
    form.append("direccion", empresa.direccion || "");
    form.append("numero_decimales", String(empresa.numero_decimales ?? 6));
    form.append("email_notificacion", empresa.email_notificacion || "");
    form.append("regimen_tipo", regimen_tipo);
    form.append("contribuyente_rimpe", String(!!empresa.contribuyente_rimpe));

    if (empresa.logo_empresa && typeof empresa.logo_empresa !== "string") {
      form.append("logo_empresa", empresa.logo_empresa);
    }

    try {
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: form,
      });

      const data = await res.json();

      if (res.ok) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Datos guardados correctamente",
        });

        if (data.id && !isEdit) {
          setEmpresa((p) => ({ ...p, id: data.id }));
        }

        const r2 = await fetch("/enterprise/empresa/obtener/", {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        const d2 = await r2.json();

        if (d2?.empresa) {
          hydrateEmpresa(d2.empresa);
        }

        if (logoRef.current) {
          logoRef.current.value = "";
        }
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: data?.error
            ? typeof data.error === "string"
              ? data.error
              : JSON.stringify(data.error)
            : "Error al guardar",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Error de conexión con el servidor",
      });
    }
  };

  const fileLabel = (f) => {
    if (!f) return "Imagen no seleccionada";
    if (typeof f === "string") return f.split("/").pop();
    return f.name || "Imagen seleccionada";
  };

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
      }}
    >
      <EnterpriseForm
        empresa={empresa}
        setEmpresa={setEmpresa}
        handleChange={handleChange}
        handleCheck={handleCheck}
        handleFile={handleFile}
        fileLabel={fileLabel}
        openPreview={openPreview}
        submit={submit}
        logoRef={logoRef}
      />

      <EnterpriseModals
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        openModal={openModal}
        preview={preview}
        closePreview={closePreview}
      />
    </Paper>
  );
};

export default EnterpriseConfig;